import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, MapPin, Battery, User, Phone, CheckCircle2, X } from 'lucide-react';
import { toast } from 'sonner';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import AutoTopoTileLayer from './AutoTopoTileLayer';
import 'leaflet/dist/leaflet.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Banner permanente para admin/staff que mostra os alertas SOS activos.
 *
 * Comportamento:
 * - Faz polling a /api/sos/alerts a cada 10s.
 * - Quando aparece um alerta NOVO → toast + abre modal automaticamente.
 * - Mostra badge vermelho fixo no topo do dashboard com o nº de alertas.
 * - Modal: mapa Leaflet, mensagem, bateria, accuracy, botões Ack / Resolver.
 */
const SOSAlertsBanner = ({ user }) => {
  const isStaffOrAdmin = user?.role === 'admin' || user?.role === 'staff';
  const [alerts, setAlerts] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [focused, setFocused] = useState(null);
  const knownIdsRef = useRef(new Set());
  const audioRef = useRef(null);

  // Pre-load short alert beep (simple Web Audio API on demand)
  const beep = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = 880;
      gain.gain.value = 0.18;
      osc.start();
      setTimeout(() => { osc.stop(); ctx.close().catch(() => {}); }, 250);
    } catch { /* ignore */ }
  }, []);

  const fetchAlerts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/sos/alerts`, {
        params: { only_active: true },
        withCredentials: true,
      });
      const incoming = data || [];
      // Detect new ones
      const newAlerts = incoming.filter((a) => !knownIdsRef.current.has(a.sos_id));
      if (newAlerts.length > 0) {
        beep();
        toast.error(`🆘 Novo SOS de ${newAlerts[0].user_name}`, {
          description: newAlerts[0].message || 'Toca para ver detalhes',
          action: {
            label: 'Ver',
            onClick: () => { setFocused(newAlerts[0]); setModalOpen(true); },
          },
        });
        // Auto-open modal for the first new alert
        setFocused(newAlerts[0]);
        setModalOpen(true);
      }
      knownIdsRef.current = new Set(incoming.map((a) => a.sos_id));
      setAlerts(incoming);
    } catch { /* silent */ }
  }, [beep]);

  useEffect(() => {
    if (!isStaffOrAdmin) return undefined;
    // Fev/2026 — SOS é SAFETY-CRITICAL, poll SEMPRE mesmo com tab escondida.
    // Só o hook de visibilidade fica ainda importado se um dia usarmos
    // para outros polls não-críticos neste ficheiro.
    fetchAlerts();
    const i = setInterval(fetchAlerts, 10000);
    return () => clearInterval(i);
  }, [isStaffOrAdmin, fetchAlerts]);

  if (!isStaffOrAdmin || alerts.length === 0) return null;

  const ackAlert = async (sosId) => {
    try {
      await axios.post(`${API}/sos/${sosId}/ack`, {}, { withCredentials: true });
      toast.success('Alerta reconhecido');
      fetchAlerts();
    } catch (e) {
      toast.error('Falha ao reconhecer');
    }
  };

  const resolveAlert = async (sosId) => {
    try {
      await axios.post(`${API}/sos/${sosId}/resolve`, {}, { withCredentials: true });
      toast.success('Alerta resolvido');
      setModalOpen(false);
      fetchAlerts();
    } catch (e) {
      toast.error('Falha ao resolver');
    }
  };

  const respondAlert = async (sosId) => {
    try {
      await axios.post(`${API}/sos/${sosId}/respond`, {}, { withCredentials: true });
      toast.success('🚙 Marcado como a caminho — os membros do grupo foram notificados');
      fetchAlerts();
    } catch (e) {
      toast.error('Falha ao responder');
    }
  };

  const cancelRespond = async (sosId) => {
    try {
      await axios.delete(`${API}/sos/${sosId}/respond`, { withCredentials: true });
      toast.info('Removido da lista de respondentes');
      fetchAlerts();
    } catch (e) {
      toast.error('Falha');
    }
  };

  const myResponderId = user?.user_id;
  const iAmResponding = (alert) =>
    (alert?.responders || []).some((r) => r.user_id === myResponderId);

  return (
    <>
      {/* Top fixed banner — admin/staff only when there are active alerts */}
      <div
        className="fixed top-3 right-3 z-50 bg-rose-600 text-white px-4 py-2 rounded-xl shadow-2xl flex items-center gap-3 animate-pulse-slow cursor-pointer hover:bg-rose-700 transition"
        onClick={() => { setFocused(alerts[0]); setModalOpen(true); }}
        data-testid="sos-banner"
      >
        <AlertTriangle className="w-5 h-5" />
        <div>
          <div className="font-black text-sm">{alerts.length} {alerts.length === 1 ? 'SOS activo' : 'SOS activos'}</div>
          <div className="text-xs text-rose-100 truncate max-w-[200px]">
            {alerts[0]?.user_name} · {alerts[0]?.message || 'sem mensagem'}
          </div>
        </div>
        <Badge variant="outline" className="bg-white text-rose-700 border-0 font-black">
          {alerts.length}
        </Badge>
      </div>

      {/* Detail modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl border-t-8 border-t-rose-600">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-rose-700">
              <AlertTriangle className="w-7 h-7 animate-pulse" />
              🆘 SOS de {focused?.user_name}
            </DialogTitle>
            <DialogDescription>
              {focused?.user_email} · Disparado {focused?.created_at && new Date(focused.created_at).toLocaleString('pt-PT')}
            </DialogDescription>
          </DialogHeader>

          {/* Message */}
          {focused?.message && (
            <div className="bg-rose-50 border-l-4 border-rose-600 p-3 rounded-r-lg">
              <div className="text-[10px] font-black text-rose-900 uppercase tracking-wider">Mensagem do utilizador</div>
              <div className="text-base italic text-slate-800 mt-1">&ldquo;{focused.message}&rdquo;</div>
            </div>
          )}

          {/* Stats grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            <div className="bg-slate-50 p-2 rounded">
              <div className="flex items-center gap-1 text-xs text-slate-500 font-bold uppercase"><MapPin className="w-3 h-3" /> Coords</div>
              <div className="font-mono text-xs mt-1 select-all">{focused?.lat?.toFixed(5)}, {focused?.lon?.toFixed(5)}</div>
            </div>
            {focused?.accuracy != null && (
              <div className="bg-slate-50 p-2 rounded">
                <div className="text-xs text-slate-500 font-bold uppercase">Precisão</div>
                <div className="font-black mt-1">±{Math.round(focused.accuracy)} m</div>
              </div>
            )}
            {focused?.battery != null && (
              <div className="bg-slate-50 p-2 rounded">
                <div className="flex items-center gap-1 text-xs text-slate-500 font-bold uppercase"><Battery className="w-3 h-3" /> Bateria</div>
                <div className={`font-black mt-1 ${focused.battery < 20 ? 'text-rose-600' : ''}`}>{Math.round(focused.battery)}%</div>
              </div>
            )}
            <div className="bg-slate-50 p-2 rounded">
              <div className="text-xs text-slate-500 font-bold uppercase">Estado</div>
              <div className="font-black mt-1">
                {focused?.status === 'acked' ? '✓ Reconhecido' : '⚠ Activo'}
              </div>
            </div>
          </div>

          {/* Map */}
          {focused?.lat != null && (
            <div className="h-[300px] rounded-lg overflow-hidden border border-slate-200">
              <MapContainer
                center={[focused.lat, focused.lon]}
                zoom={14}
                maxZoom={19}
                style={{ height: '100%', width: '100%' }}
                key={focused.sos_id}
              >
                <AutoTopoTileLayer />
                <CircleMarker
                  center={[focused.lat, focused.lon]}
                  radius={16}
                  pathOptions={{ color: '#DC2626', weight: 3, fillColor: '#DC2626', fillOpacity: 0.7 }}
                >
                  <Popup>
                    <strong>🆘 {focused.user_name}</strong>
                    <br />{focused.message || 'sem mensagem'}
                  </Popup>
                </CircleMarker>
              </MapContainer>
            </div>
          )}

          {/* Actions */}
          {/* Responders list — quem já está a caminho */}
          {focused?.responders && focused.responders.length > 0 && (
            <div className="bg-emerald-50 border-l-4 border-emerald-600 p-3 rounded-r-lg">
              <div className="text-[10px] font-black text-emerald-900 uppercase tracking-wider mb-1">
                🚙 A caminho ({focused.responders.length})
              </div>
              <div className="flex flex-wrap gap-2">
                {focused.responders.map((r) => (
                  <span
                    key={r.user_id}
                    className="bg-white border border-emerald-300 text-emerald-800 text-xs font-bold px-2 py-1 rounded-full"
                  >
                    {r.name} {r.role === 'admin' ? '👮' : '🥷'}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${focused?.lat},${focused?.lon}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1"
            >
              <Button className="w-full bg-blue-600 hover:bg-blue-700 gap-2">
                <MapPin className="w-4 h-4" /> Abrir Google Maps
              </Button>
            </a>
            {focused?.user_email && (
              <a href={`mailto:${focused.user_email}`} className="flex-1">
                <Button variant="outline" className="w-full gap-2">
                  <User className="w-4 h-4" /> Enviar email
                </Button>
              </a>
            )}
          </div>

          {/* "Estou a caminho" — admin/staff respondem */}
          {focused && (
            iAmResponding(focused) ? (
              <Button
                variant="outline"
                className="w-full border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                onClick={() => cancelRespond(focused.sos_id)}
                data-testid="sos-cancel-respond-btn"
              >
                ✓ Estás a caminho — clica para cancelar
              </Button>
            ) : (
              <Button
                className="w-full bg-emerald-700 hover:bg-emerald-800 text-white gap-2"
                onClick={() => respondAlert(focused.sos_id)}
                data-testid="sos-respond-btn"
              >
                🚙 Estou a caminho (notificar grupo)
              </Button>
            )
          )}

          <div className="flex gap-2">
            {focused?.status !== 'acked' && (
              <Button
                className="flex-1 bg-amber-500 hover:bg-amber-600 gap-2"
                onClick={() => ackAlert(focused.sos_id)}
                data-testid="sos-ack-btn"
              >
                <CheckCircle2 className="w-4 h-4" /> Reconhecer
              </Button>
            )}
            <Button
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 gap-2"
              onClick={() => resolveAlert(focused.sos_id)}
              data-testid="sos-resolve-btn"
            >
              <CheckCircle2 className="w-4 h-4" /> Resolver e fechar
            </Button>
          </div>

          {/* Other active alerts */}
          {alerts.length > 1 && (
            <div className="border-t pt-3">
              <div className="text-xs font-black uppercase text-slate-600 mb-2">
                Outros alertas activos ({alerts.length - 1})
              </div>
              <div className="space-y-1">
                {alerts.filter((a) => a.sos_id !== focused?.sos_id).map((a) => (
                  <button
                    key={a.sos_id}
                    onClick={() => setFocused(a)}
                    className="w-full text-left flex items-center gap-2 px-3 py-2 bg-rose-50 hover:bg-rose-100 rounded text-sm"
                  >
                    <AlertTriangle className="w-4 h-4 text-rose-600" />
                    <span className="font-bold">{a.user_name}</span>
                    <span className="text-slate-500 truncate">{a.message || '(sem mensagem)'}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SOSAlertsBanner;
